import { useReportLoading, useReportRange } from '../shared/ReportLead';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    Typography
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ReactApexChart from 'react-apexcharts';
import { NumericFormat } from 'react-number-format';
import CurrencyFormat from 'react-currency-format';
import { BsHouseDoor } from 'react-icons/bs';
import Comments from '../comments';
import ReportToolbar from '../ReportPanel/toolbar';
import TableCard from '../../system/table-card';
import ReportQuestionAssistant from '../../system/controls/report-question-assistant';
import ReportOverviewHero from '../shared/reportOverviewHero';
import {
    getPlainSurfaceCardSx,
    getReportBackdropSx,
    getReportCanvasSx,
    getReportTabsShellSx
} from '../shared/reportSurfaceStyles';
import { AppVarsContext } from '../../context/AppVarsContext';
import { resolveShowCost } from '../../system/report-visibility';
import { formatUsd } from '../../system/report-formatters';

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const safeParse = (value, fallback = {}) => {
    if (!value) return fallback;
    if (typeof value === 'object') return value;
    if (typeof value === 'string') {
        try {
            return JSON.parse(value);
        } catch (_) {
            return fallback;
        }
    }
    return fallback;
};

const parseResponse = async (response) => {
    const json = await response.json().catch(() => null);
    if (!response.ok) {
        throw new Error(json?.detail || json?.error || `Request failed (${response.status})`);
    }
    return json;
};

const hasConfiguredValue = (value) => {
    if (Array.isArray(value)) return value.length > 0;
    return Boolean(value);
};

const formatMetric = (format, value) => {
    const next = toNumber(value);
    switch (format) {
        case 'currency':
            return formatUsd(next);
        case 'percent':
            return `${next.toFixed(2)}%`;
        default:
            return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(next);
    }
};

const chartMetricChoices = [
    { key: 'IMPRESSIONS', label: 'Impressions', format: 'number', color: '#4f46e5', costSensitive: false },
    { key: 'CLICKS', label: 'Clicks', format: 'number', color: '#0f766e', costSensitive: false },
    { key: 'CONVERSIONS', label: 'Conversions', format: 'number', color: '#c2410c', costSensitive: false },
    { key: 'SPEND', label: 'Spend', format: 'currency', color: '#7c3aed', costSensitive: true }
];

function TabPanel({ children, value, index, ...other }) {
    return (
        <Box role="tabpanel" hidden={value !== index} {...other}>
            {value === index ? children : null}
        </Box>
    );
}

const buildNumberColumn = (header, key, options = {}) => ({
    header,
    accessorKey: key,
    grow: true,
    minSize: options.minSize || 90,
    maxSize: options.maxSize || 130,
    Cell: ({ cell }) => (
        <NumericFormat
            decimalScale={options.decimalScale ?? 0}
            thousandSeparator=","
            value={toNumber(cell.getValue())}
            displayType="text"
            suffix={options.suffix || ''}
        />
    )
});

const buildCurrencyColumn = (header, key, options = {}) => ({
    header,
    accessorKey: key,
    grow: true,
    minSize: options.minSize || 110,
    maxSize: options.maxSize || 140,
    Cell: ({ cell }) => (
        <CurrencyFormat
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator=","
            value={toNumber(cell.getValue())}
            displayType="text"
            prefix="$"
        />
    )
});

const NEXTDOOR_ASSISTANT_VIEWS = ['Campaigns', 'Ads', 'Notes'];

export default function Nextdoor(props) {
    const theme = useTheme();
    const { appVars, appLoading } = useContext(AppVarsContext);
    const [loading, setLoading] = useState(true);
    useReportLoading(loading, props.setLoading);
    const [error, setError] = useState('');
    const setParentLoading = props.setLoading;
    const reportTitle = props.title;
    const requestId = useRef(0);
    const [tabValue, setTabValue] = useState(0);
    const [selectedMetric, setSelectedMetric] = useState('IMPRESSIONS');
    const [summaryData, setSummaryData] = useState(null);
    const [campaignRows, setCampaignRows] = useState([]);
    const [adRows, setAdRows] = useState([]);

    const { sDate, eDate } = useReportRange();
    const cid = sessionStorage.getItem('rid') || sessionStorage.getItem('cid');
    const reportProfile = props.reportProfile || {};
    const showCost = resolveShowCost(reportProfile);
    const settings = useMemo(() => safeParse(reportProfile?.settings, {}), [reportProfile?.settings]);
    const nextdoorSettings = settings?.ad_sets?.nextdoor || {};
    const canvasSx = getReportCanvasSx(theme);
    const plainCardSx = getPlainSurfaceCardSx(theme);
    const tabsShellSx = getReportTabsShellSx(theme);

    const fetchReport = useCallback(async () => {
        if (!appVars?.api_url || !cid) { setLoading(false); return; }
        const activeRequest = ++requestId.current;
        setLoading(true);
        setError('');

        try {
            if (!hasConfiguredValue(nextdoorSettings?.id || nextdoorSettings?.advertiser_id)) {
                setSummaryData(null);
                setCampaignRows([]);
                setAdRows([]);
                setError('No Nextdoor advertiser is configured for this client.');
                setLoading(false);
                setParentLoading?.(false);
                return;
            }

            const payload = {
                cid,
                aid: nextdoorSettings?.id || nextdoorSettings?.advertiser_id,
                sdate: sDate,
                edate: eDate
            };

            const [summary, campaigns, ads] = await Promise.all([
                fetch(`${appVars.api_url}/nextdoor/data`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                }).then(parseResponse),
                fetch(`${appVars.api_url}/nextdoor/campaigns`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                }).then(parseResponse),
                fetch(`${appVars.api_url}/nextdoor/ads`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                }).then(parseResponse)
            ]);

            if (requestId.current !== activeRequest) return;
            setSummaryData(summary);
            setCampaignRows(Array.isArray(campaigns?.rows) ? campaigns.rows : []);
            setAdRows(Array.isArray(ads?.rows) ? ads.rows : []);
        } catch (fetchError) {
            if (requestId.current !== activeRequest) return;
            console.error('Error fetching Nextdoor report data:', fetchError);
            setError(fetchError?.message || 'Failed to fetch Nextdoor report data.');
        } finally {
            if (requestId.current === activeRequest) setLoading(false);
        }
    }, [appVars?.api_url, cid, nextdoorSettings?.advertiser_id, nextdoorSettings?.id, setParentLoading, sDate, eDate]);

    useEffect(() => {
        if (appLoading) return;
        fetchReport();
    }, [appLoading, fetchReport]);

    useEffect(() => { const requests = requestId; return () => { ++requests.current; }; }, []);

    const totals = useMemo(() => summaryData?.totals || {}, [summaryData?.totals]);
    const timeseries = useMemo(() => Array.isArray(summaryData?.timeseries) ? summaryData.timeseries : [], [summaryData?.timeseries]);
    const visibleChartMetricChoices = useMemo(
        () => chartMetricChoices.filter((choice) => showCost || !choice.costSensitive),
        [showCost]
    );
    const activeMetric = useMemo(
        () => visibleChartMetricChoices.find((choice) => choice.key === selectedMetric) || visibleChartMetricChoices[0] || chartMetricChoices[0],
        [selectedMetric, visibleChartMetricChoices]
    );

    useEffect(() => {
        if (!showCost && activeMetric?.costSensitive) {
            setSelectedMetric('IMPRESSIONS');
        }
    }, [activeMetric?.costSensitive, showCost]);

    const campaignColumns = useMemo(() => ([
        {
            header: 'Campaign',
            accessorKey: 'CAMPAIGN',
            minSize: 260,
            grow: true,
            Cell: ({ cell }) => (
                <Typography textAlign="left" fontSize={13} fontWeight={800}>
                    {cell.row.original.CAMPAIGN || 'Untitled campaign'}
                </Typography>
            )
        },
        { header: 'Objective', accessorKey: 'OBJECTIVE', minSize: 150, grow: true },
        { header: 'Status', accessorKey: 'STATUS', minSize: 120, grow: true },
        buildNumberColumn('Impressions', 'IMPRESSIONS'),
        buildNumberColumn('Clicks', 'CLICKS'),
        buildNumberColumn('Conversions', 'CONVERSIONS'),
        buildNumberColumn('CTR', 'CTR', { decimalScale: 2, suffix: '%' }),
        ...(showCost ? [
            buildCurrencyColumn('Spend', 'SPEND'),
            buildCurrencyColumn('CPC', 'CPC'),
            buildCurrencyColumn('CPM', 'CPM'),
            buildCurrencyColumn('CPA', 'CPA')
        ] : [])
    ]), [showCost]);

    const adColumns = useMemo(() => ([
        {
            header: 'Ad',
            accessorKey: 'AD',
            minSize: 240,
            grow: true,
            Cell: ({ cell }) => (
                <Typography textAlign="left" fontSize={13} fontWeight={800}>
                    {cell.row.original.AD || 'Untitled ad'}
                </Typography>
            )
        },
        { header: 'Ad Group', accessorKey: 'ADGROUP', minSize: 170, grow: true },
        { header: 'Campaign', accessorKey: 'CAMPAIGN', minSize: 220, grow: true },
        { header: 'Status', accessorKey: 'STATUS', minSize: 120, grow: true },
        buildNumberColumn('Impressions', 'IMPRESSIONS'),
        buildNumberColumn('Clicks', 'CLICKS'),
        buildNumberColumn('Conversions', 'CONVERSIONS'),
        buildNumberColumn('CTR', 'CTR', { decimalScale: 2, suffix: '%' }),
        ...(showCost ? [
            buildCurrencyColumn('Spend', 'SPEND'),
            buildCurrencyColumn('CPC', 'CPC'),
            buildCurrencyColumn('CPM', 'CPM'),
            buildCurrencyColumn('CPA', 'CPA')
        ] : [])
    ]), [showCost]);

    const chartOptions = useMemo(() => ({
        chart: {
            type: activeMetric?.key === 'SPEND' ? 'area' : 'line',
            toolbar: { show: false },
            zoom: { enabled: false }
        },
        colors: [activeMetric.color],
        stroke: {
            curve: 'smooth',
            width: 3
        },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.35,
                opacityTo: 0.05
            }
        },
        dataLabels: { enabled: false },
        xaxis: {
            type: 'datetime',
            labels: { datetimeUTC: false }
        },
        yaxis: {
            labels: {
                formatter: (value) => activeMetric.format === 'currency'
                    ? formatUsd(value)
                    : Number(value || 0).toLocaleString()
            }
        },
        tooltip: {
            x: { format: 'MMM dd, yyyy' },
            y: {
                formatter: (value) => formatMetric(activeMetric.format, value)
            }
        },
        grid: {
            borderColor: alpha(theme.palette.primary.main, 0.12)
        }
    }), [activeMetric, theme]);

    const chartSeries = useMemo(() => ([{
        name: activeMetric.label,
        data: timeseries.map((row) => ({
            x: row.DATE,
            y: toNumber(row?.[activeMetric.key])
        }))
    }]), [activeMetric, timeseries]);

    const assistantConfig = useMemo(() => ({
        activeView: NEXTDOOR_ASSISTANT_VIEWS[tabValue] || 'Campaigns',
        totals,
        account: summaryData?.account || {},
        campaigns: campaignRows.slice(0, 20),
        ads: adRows.slice(0, 20),
        selectedMetric,
    }), [adRows, campaignRows, selectedMetric, summaryData?.account, tabValue, totals]);

    return (
        <Box sx={{ position: 'relative', minHeight: '100%' }}>
            <Box sx={getReportBackdropSx(theme)} />
            <Box sx={canvasSx}>
                <ReportToolbar
                    client={{ name: reportProfile?.clientname || reportProfile?.name || props?.title || 'Nextdoor' }}
                    profile={reportProfile}
                    loading={loading}
                    title={props?.title || 'Nextdoor'}
                />

                {loading ? <LinearProgress /> : null}
                {error ? (
                    <Alert severity="error">
                        {error}
                    </Alert>
                ) : null}

                <ReportOverviewHero
                    eyebrow="Nextdoor Ads Report"
                    title="Neighborhood Performance"
                    subtitle={showCost
                        ? 'Delivery, response, and efficiency across the connected Nextdoor advertiser.'
                        : 'Delivery, clicks, and conversions across the connected Nextdoor advertiser.'}
                    loading={loading}
                    loadingLabel="Refreshing Nextdoor report data"
                    chips={[
                        { label: 'Account', value: summaryData?.account?.account_name || nextdoorSettings?.account_name || 'Nextdoor' },
                        { label: 'Range', value: `${sessionStorage.getItem('sDate')} to ${sessionStorage.getItem('eDate')}` }
                    ]}
                    metrics={[
                        { label: 'Impressions', value: totals.IMPRESSIONS, helper: 'Delivered neighborhood reach' },
                        { label: 'Clicks', value: totals.CLICKS, helper: 'Response volume from ads' },
                        { label: 'Conversions', value: totals.CONVERSIONS, helper: 'Attributed actions recorded by Nextdoor' },
                        ...(showCost ? [{ label: 'Spend', value: totals.SPEND, format: 'currency', helper: 'Billable media spend' }] : []),
                        { label: 'CTR', value: totals.CTR, format: 'percent', helper: showCost ? `CPC ${formatMetric('currency', totals.CPC)}` : 'Click-through rate' },
                        ...(showCost ? [{ label: 'CPA', value: totals.CPA, format: 'currency', helper: `CVR ${formatMetric('percent', totals.CVR)}` }] : []),
                        { label: 'Campaigns', value: campaignRows.length, helper: 'Campaigns with report data' },
                        { label: 'Ads', value: adRows.length, helper: 'Ads with report data' }
                    ]}
                />

                <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2}>
                    <Card sx={{ ...plainCardSx, flex: 1.7 }}>
                        <CardContent sx={{ display: 'grid', gap: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                        Trend Line
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Day-by-day delivery and response across the selected date range.
                                    </Typography>
                                </Box>
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <InputLabel id="nextdoor-metric-label">Metric</InputLabel>
                                    <Select
                                        labelId="nextdoor-metric-label"
                                        value={selectedMetric}
                                        label="Metric"
                                        onChange={(event) => setSelectedMetric(event.target.value)}
                                    >
                                        {visibleChartMetricChoices.map((choice) => (
                                            <MenuItem key={choice.key} value={choice.key}>{choice.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                            <ReactApexChart options={chartOptions} series={chartSeries} type={activeMetric?.key === 'SPEND' ? 'area' : 'line'} height={320} />
                        </CardContent>
                    </Card>

                    <Card sx={{ ...plainCardSx, flex: 1 }}>
                        <CardContent sx={{ display: 'grid', gap: 1.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <BsHouseDoor size={18} color={theme.palette.primary.main} />
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                    Snapshot
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {showCost
                                    ? 'Nextdoor’s reporting surface is strongest on spend, delivery, clicks, and attributed conversions. This report keeps the focus on efficiency and response quality rather than revenue-based ROAS.'
                                    : 'Nextdoor’s reporting surface is strongest on delivery, clicks, and attributed conversions. This report keeps the focus on response quality and conversion volume.'}
                            </Typography>
                            {[
                                ['CVR', formatMetric('percent', totals.CVR)],
                                ['Campaigns', formatMetric('number', campaignRows.length)],
                                ['Ads', formatMetric('number', adRows.length)]
                            ].concat(showCost ? [
                                ['CPM', formatMetric('currency', totals.CPM)],
                                ['CPA', formatMetric('currency', totals.CPA)]
                            ] : []).map(([label, value]) => (
                                <Box
                                    key={label}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        px: 1.25,
                                        py: 1,
                                        borderRadius: 2,
                                        bgcolor: alpha(theme.palette.primary.main, 0.05)
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{value}</Typography>
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                </Stack>

                <Card sx={tabsShellSx}>
                    <Tabs value={tabValue} onChange={(_, value) => setTabValue(value)} variant="scrollable" scrollButtons="auto">
                        <Tab label="Campaigns" />
                        <Tab label="Ads" />
                        <Tab label="Notes" />
                    </Tabs>
                </Card>

                <TabPanel value={tabValue} index={0}>
                    <Box sx={plainCardSx}>
                        <TableCard
                            title="Nextdoor Campaign Performance"
                            data={campaignRows}
                            columns={campaignColumns}
                            reportProfile={reportProfile}
                            appendDefaultMetrics={false}
                            compact={false}
                        />
                    </Box>
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    <Box sx={plainCardSx}>
                        <TableCard
                            title="Nextdoor Ad Performance"
                            data={adRows}
                            columns={adColumns}
                            reportProfile={reportProfile}
                            appendDefaultMetrics={false}
                            compact={false}
                        />
                    </Box>
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                    <Box sx={{ ...plainCardSx, p: 1.5 }}>
                        <Comments
                            sDate={sessionStorage.getItem('sDate')}
                            eDate={sessionStorage.getItem('eDate')}
                            color={theme.palette.primary.main}
                            admintoken={0}
                            view="a7edd5c0-7a77-49ed-86ad-6fe3066a0f59"
                            type="nextdoor"
                            setLoading={props.setLoading}
                        />
                    </Box>
                </TabPanel>

                <ReportQuestionAssistant
                    reportType="nextdoor"
                    reportTitle="Nextdoor"
                    profile={reportProfile}
                    contextPayload={assistantConfig}
                />
            </Box>
        </Box>
    );
}
